/**
 * A ponte com o design system do macOS Tahoe.
 *
 * `vendor/macos/js/macos.js` é um IIFE que pendura `window.MacOS`. Em vez de
 * espalhar `(window as any).MacOS` pela interface, o acesso passa por aqui,
 * com tipo — e com o cuidado de nunca explodir se o design system não tiver
 * carregado (o aplicativo continua utilizável, só sem os enfeites).
 */

export interface ItemDeMenu {
  label?: string;
  icon?: string;
  value?: string;
  checked?: boolean;
  disabled?: boolean;
  destructive?: boolean;
  shortcut?: string;
}

export type EntradaDeMenu = ItemDeMenu | '-';

interface ApiMacOs {
  Theme: {
    prefs: Record<string, unknown>;
    set(chave: string, valor: unknown): void;
    setMany(prefs: Record<string, unknown>): void;
  };
  Menu: {
    open(
      ancora: HTMLElement,
      itens: EntradaDeMenu[],
      opcoes?: { onSelect?: (valor: string, item: HTMLElement) => void; placement?: string },
    ): HTMLElement;
    close(): void;
  };
  Popover: { toggle(ancora: HTMLElement, alvo: string | HTMLElement, lado?: string): void };
  Notify: {
    show(o: {
      app?: string | undefined;
      icon?: string | undefined;
      iconClass?: string | undefined;
      title: string;
      body?: string | undefined;
      time?: string | undefined;
      duration?: number | undefined;
      dark?: boolean | undefined;
      actions?: Array<{ label: string; onClick?: (() => void) | undefined }> | undefined;
    }): void;
  };
  init(raiz?: unknown): void;
  closeAll(): void;
}

function api(): ApiMacOs | null {
  return (globalThis as unknown as { MacOS?: ApiMacOs }).MacOS ?? null;
}

/** Tema e aparência (persistidos pelo design system em localStorage). */
export const tema = {
  definir(chave: 'theme' | 'accent' | 'iconStyle', valor: string): void {
    api()?.Theme.set(chave, valor);
  },
  definirVarios(prefs: Record<string, unknown>): void {
    api()?.Theme.setMany(prefs);
  },
  atual(chave: string): unknown {
    return api()?.Theme.prefs[chave];
  },
};

/** Menu suspenso ancorado num botão. */
export function abrirMenu(
  ancora: HTMLElement,
  itens: EntradaDeMenu[],
  aoEscolher: (valor: string) => void,
): void {
  const m = api();
  if (!m) return;
  m.Menu.open(ancora, itens, { onSelect: (valor) => aoEscolher(valor) });
}

/** Notificação no canto — o mesmo banner do sistema. */
export function notificar(o: {
  titulo: string;
  corpo?: string | undefined;
  icone?: string | undefined;
  duracao?: number | undefined;
  acoes?: Array<{ rotulo: string; aoClicar: () => void }> | undefined;
}): void {
  api()?.Notify.show({
    app: 'PCP',
    title: o.titulo,
    body: o.corpo ?? '',
    icon: o.icone ?? 'checkmark-circle',
    duration: o.duracao ?? 4200,
    actions: o.acoes?.map((a) => ({ label: a.rotulo, onClick: a.aoClicar })),
  });
}

export function fecharCamadas(): void {
  api()?.closeAll();
}

/** O design system está carregado? */
export function temDesignSystem(): boolean {
  return api() !== null;
}
